import { useMemo, useState, useCallback } from "react"
import { Col, Row, UncontrolledTooltip, Button, Badge, Input, Label, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import { useListParams } from "hooks/useListParams"
import { useAccountTransactions, useAccountTransaction, useReverseAccountTransaction } from "hooks/useAccountTransaction"
import { useAccounts } from "hooks/useAccountEntity"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"
import { downloadAndOpenFile } from "api/pdfApi"
import AccountFilterPanel from "pages/AccountPages/utils/AccountFilterPanel"
import ReversalBadge from "pages/AccountPages/utils/ReversalBadge"
import ReferenceContextInfo from "pages/AccountPages/utils/ReferenceContextInfo"
import { humanizeReferenceType } from "pages/AccountPages/utils/referenceTypeLabels"
import { getActiveRecords, getRecordStatus, getReversalEntries, sumAmounts } from "pages/AccountPages/utils/reversalHelpers"
import WorkspaceStatCard from "pages/AccountPages/AccountWorkspace/components/WorkspaceStatCard"

import AccountTransactionHeader from "./AccountTransactionHeader"
import AccountTransactionView from "./AccountTransactionView"

const AccountTransactionList = () => {
  document.title = "Account Transaction Ledger"

  const [viewModal, setViewModal] = useState(false)
  const [selectedViewId, setSelectedViewId] = useState(null)
  const [reverseModal, setReverseModal] = useState(false)
  const [selectedReverseItem, setSelectedReverseItem] = useState(null)
  const [filters, setFilters] = useState({ date_from: "", date_to: "", account_id: "", status: "" })
  const [isExporting, setIsExporting] = useState(false)
  const [isExportingCsv, setIsExportingCsv] = useState(false)
  const { mutate: reverseTransaction, isPending: isReversing } = useReverseAccountTransaction()

  const { params, setPage, setSearch, resetParams, hasActiveFilters } = useListParams({ page: 1, search: "" })

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleFilterReset = useCallback(() => {
    setFilters({ date_from: "", date_to: "", account_id: "", status: "" })
  }, [])

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return {
      page,
      per_page,
      search,
      ...(filters.date_from && { date_from: filters.date_from }),
      ...(filters.date_to && { date_to: filters.date_to }),
      ...(filters.account_id && { account_id: filters.account_id }),
    }
  }, [params, filters])

  const { data: responseData, isLoading, refetch, isRefetching } =
    useAccountTransactions(queryParams)
  const { data: viewResponse, isLoading: isViewLoading } = useAccountTransaction(selectedViewId, viewModal)

  const handleExportPdf = () =>
    downloadAndOpenFile({ url: "/account-exports/transactions", params: queryParams, openInNewTab: false, filename: "transactions.pdf", onStart: () => setIsExporting(true), onEnd: () => setIsExporting(false) })

  const handleExportCsv = () =>
    downloadAndOpenFile({ url: "/account-exports/transactions/csv", params: queryParams, mimeType: "text/csv", openInNewTab: false, filename: "transactions.csv", onStart: () => setIsExportingCsv(true), onEnd: () => setIsExportingCsv(false) })

  const { data: accountResponse } = useAccounts({ all: true, per_page: 200 })
  const accountList = accountResponse?.data?.data || accountResponse?.data || []

  const toggleViewModal = useCallback(() => {
    setViewModal(prev => !prev)
    if (viewModal) setSelectedViewId(null)
  }, [viewModal])

  const handleViewClick = useCallback(item => {
    setSelectedViewId(item?.id || null)
    setViewModal(true)
  }, [])

  const handleReverseClick = useCallback(item => {
    setSelectedReverseItem(item)
    setReverseModal(true)
  }, [])

  const handleReverseConfirm = () => {
    if (!selectedReverseItem) return
    reverseTransaction(selectedReverseItem.id, {
      onSuccess: () => {
        setReverseModal(false)
        setSelectedReverseItem(null)
        refetch()
      },
      onError: err => {
        const msg = err?.response?.data?.message || "Failed to reverse transaction."
        alert(msg)
      },
    })
  }

  const columns = useMemo(
    () => [
      { id: "id", header: "ID", accessorKey: "id" },
      { id: "account", header: "Account", cell: ({ row }) => row.original.account?.name || "-" },
      {
        id: "type",
        header: "Type",
        cell: ({ row }) => {
          const type = row.original.transaction_type?.name || row.original.transactionType?.name || "-"
          const isCredit = type?.toLowerCase?.().includes("credit")
          return (
            <div className="d-flex flex-wrap gap-1">
              <Badge color={isCredit ? "success" : "danger"} className="px-2">{type}</Badge>
              <ReversalBadge isReversal={row.original.is_reversal} isReversed={row.original.is_reversed} className="px-2" />
            </div>
          )
        },
      },
      {
        id: "amount",
        header: "Amount",
        cell: ({ row }) => {
          const type = row.original.transaction_type?.name || row.original.transactionType?.name || ""
          const isCredit = type?.toLowerCase?.().includes("credit")
          const currency = row.original.account?.currency || "BDT"
          return (
            <span className={`fw-semibold ${isCredit ? "text-success" : "text-danger"}`}>
              {isCredit ? "+" : "-"}{formatCurrency(row.original.amount || 0, currency)}
            </span>
          )
        },
      },
      {
        id: "balance",
        header: "Balance",
        cell: ({ row }) => {
          if (row.original.running_balance == null) return "-"
          const currency = row.original.account?.currency || "BDT"
          const isNeg = row.original.running_balance < 0
          return (
            <span className={`fw-semibold font-monospace ${isNeg ? "text-danger" : "text-dark"}`}>
              {formatCurrency(row.original.running_balance, currency)}
            </span>
          )
        },
      },
      {
        id: "reference",
        header: "Reference",
        cell: ({ row }) => (
          <div className="small">
            <div>{humanizeReferenceType(row.original.reference_type)}</div>
            {row.original.reference_id && <div className="text-muted font-monospace">#{row.original.reference_id}</div>}
          </div>
        ),
      },
      {
        id: "date",
        header: "Date",
        cell: ({ row }) => row.original.date ? formatReadableDate(row.original.date, "MMM d, y") : "-",
      },
      {
        id: "description",
        header: "Description",
        cell: ({ row }) => {
          const item = row.original
          const context = <ReferenceContextInfo referenceType={item.reference_type} context={item.reference_context} />
          return (
            <div className="d-flex flex-column gap-1">
              {context}
              {item.description && <div className={context ? "text-muted small" : ""}>{item.description}</div>}
              {!context && !item.description && "-"}
            </div>
          )
        },
      },
      {
        id: "actions",
        header: "Action",
        cell: ({ row }) => {
          const item = row.original
          // Hide Reverse if: this row IS a reversal entry, OR it has already been reversed
          const canReverse = !item.is_reversal && !item.is_reversed
          return (
            <ul className="list-unstyled hstack gap-1 mb-0">
              <li>
                <Button color="primary" size="sm" onClick={() => handleViewClick(item)} id={`view-acct-txn-${item.id}`}>
                  <i className="mdi mdi-eye-outline" />
                  <span className="ms-1">View</span>
                </Button>
                <UncontrolledTooltip placement="top" target={`view-acct-txn-${item.id}`}>View</UncontrolledTooltip>
              </li>
              {canReverse && (
                <li>
                  <Button color="warning" size="sm" onClick={() => handleReverseClick(item)} id={`rev-acct-txn-${item.id}`}>
                    <i className="mdi mdi-undo-variant" />
                  </Button>
                  <UncontrolledTooltip placement="top" target={`rev-acct-txn-${item.id}`}>Reverse</UncontrolledTooltip>
                </li>
              )}
            </ul>
          )
        },
      },
    ],
    [handleViewClick, handleReverseClick]
  )

  const data = responseData?.data?.data || responseData?.data || []
  const filteredData = useMemo(() => {
    if (!filters.status) return data
    return data.filter(item => getRecordStatus(item) === filters.status)
  }, [data, filters.status])
  const paginationInfo = Array.isArray(responseData?.data) ? null : responseData?.data
  const summary = useMemo(() => {
    const postedEntries = getActiveRecords(data)
    const reversalEntries = getReversalEntries(data)
    const netMovement = filteredData.reduce((sum, item) => {
      const type = (item.transaction_type?.name || item.transactionType?.name || "").toLowerCase()
      return sum + (type.includes("credit") ? Number(item.amount || 0) : Number(item.amount || 0) * -1)
    }, 0)

    return {
      grossPosted: sumAmounts(postedEntries),
      reversalAdjustments: sumAmounts(reversalEntries),
      netMovement,
    }
  }, [data, filteredData])

  return (
    <PageWrapper title="Account Transaction Ledger" breadcrumbItem="Transaction Ledger">
      <AccountTransactionHeader
        handleRefresh={refetch}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? data.length}
        onExportPdf={handleExportPdf}
        isExporting={isExporting}
        onExportCsv={handleExportCsv}
        isExportingCsv={isExportingCsv}
      />

      <div className="pb-3" />

      <Row className="g-3 mb-3">
        <Col md={4}>
          <WorkspaceStatCard title="Gross Posted" value={formatCurrency(summary.grossPosted, "BDT")} subtitle="Visible non-reversal entries" icon="mdi mdi-book-open-page-variant-outline" color="primary" />
        </Col>
        <Col md={4}>
          <WorkspaceStatCard title="Reversal Adjustments" value={formatCurrency(summary.reversalAdjustments, "BDT")} subtitle="Counter-entries in the current page" icon="mdi mdi-swap-horizontal" color="info" />
        </Col>
        <Col md={4}>
          <WorkspaceStatCard title="Net Movement" value={formatCurrency(Math.abs(summary.netMovement), "BDT")} subtitle={summary.netMovement >= 0 ? "Net credit movement in the filtered result" : "Net debit movement in the filtered result"} icon={summary.netMovement >= 0 ? "mdi mdi-trending-up" : "mdi mdi-trending-down"} color={summary.netMovement >= 0 ? "success" : "danger"} />
        </Col>
      </Row>

      <AccountFilterPanel filters={filters} onChange={handleFilterChange} onReset={handleFilterReset}>
        <Col xs={12} sm={6} md={3}>
          <Label className="mb-1 small fw-semibold">Account</Label>
          <Input type="select" bsSize="sm" value={filters.account_id || ""} onChange={e => handleFilterChange("account_id", e.target.value)}>
            <option value="">All Accounts</option>
            {accountList.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Input>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Label className="mb-1 small fw-semibold">Status</Label>
          <Input type="select" bsSize="sm" value={filters.status || ""} onChange={e => handleFilterChange("status", e.target.value)}>
            <option value="">All Entries</option>
            <option value="active">Posted</option>
            <option value="reversed">Reversed</option>
            <option value="reversal_entry">Reversal Entry</option>
          </Input>
        </Col>
      </AccountFilterPanel>

      <Row className="mb-3">
        <Col sm={4}><GlobalInputSearch value={params.search} onChange={setSearch} /></Col>
      </Row>

      <TableContainer
        columns={columns}
        data={filteredData}
        isPagination={!!paginationInfo}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      <AccountTransactionView
        modal={viewModal}
        toggleModal={toggleViewModal}
        transaction={viewResponse?.data}
        loading={isViewLoading}
      />

      <Modal isOpen={reverseModal} toggle={() => setReverseModal(false)} centered size="sm">
        <ModalHeader toggle={() => setReverseModal(false)}>Reverse Transaction</ModalHeader>
        <ModalBody>
          <p className="mb-1">Are you sure you want to reverse this transaction?</p>
          {selectedReverseItem && (
            <p className="text-muted small mb-0">
              {selectedReverseItem.reference_type} #{selectedReverseItem.reference_id} —{" "}
              {formatCurrency(selectedReverseItem.amount, selectedReverseItem.account?.currency || "BDT")}
            </p>
          )}
          <p className="text-danger small mt-2 mb-0">This action will post counter-entries and cannot be undone.</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setReverseModal(false)}>Cancel</Button>
          <Button color="warning" onClick={handleReverseConfirm} disabled={isReversing}>
            {isReversing ? "Reversing..." : "Confirm Reversal"}
          </Button>
        </ModalFooter>
      </Modal>
    </PageWrapper>
  )
}

export default AccountTransactionList
